import { Cache, ConsoleLogger } from "aws-amplify/utils";
import { signIn, getCurrentUser, fetchAuthSession, signOut, confirmSignIn, signUp, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword } from "aws-amplify/auth";
import { patch, post, del } from "./api";
import { Amplify } from "aws-amplify";
import config from "../config";
import {
  AUTH_USER,
  AUTH_MFA,
  AUTH_NEW_PASSWORD_REQUIRED,
  UNAUTH_USER,
  REGISTER_USER,
  REGISTER_USER_CONFIRM,
  CODE_RESEND_SUCCESS,
  REGISTER_USER_ERROR,
  FORGOT_PASSWORD,
  FORGOT_PASSWORD_CONFIRM,
  AUTH_ERROR,
  RESET_APP,
  AUTH_CONFIRM_SUCCESS,
} from "./types";
import dexie from "dexie";

const logger = new ConsoleLogger("authActions");

export function authError(error) {
  if (
    error.code === "InvalidParameterException" &&
    error.message.indexOf("password") !== -1
  ) {
    error.message = "Please enter a valid password!";
  }

  return {
    type: AUTH_ERROR,
    payload: error.message ? error.message : error,
  };
}

// Cognito - signIn()
export function login({ username, password }, navigate) {
  return async function (dispatch) {
    try {
      logger.info("actions.login(): username:", { username });
      const db = new dexie("Jobs");
      await db.delete();
      Cache.clear();

      const { nextStep } = await signIn({ username, password });

      // Cache.removeItem("lastpath");
      // Cache.removeItem("previouspath");
      // console.log(nextStep.signInStep)
      if (nextStep.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        dispatch({ type: AUTH_NEW_PASSWORD_REQUIRED, payload: { username } });
        return "NEW_PASSWORD_REQUIRED";
      } else if (nextStep.signInStep === 'CONTINUE_SIGN_IN_WITH_MFA_SELECTION') {
        dispatch({ type: AUTH_MFA, payload: { username } });
        return "MFA_REQUIRED";
      } else if (nextStep.signInStep === 'DONE') {
        const currentAuthUser = await getCurrentUser();
        dispatch({ type: AUTH_USER, currentAuthUser });

        // console.log("just before login patch")

        await patch("/user/updatedLastAccessedCount", {}).catch((error) => {
          logger.error("login():updatedLastAccessed err:", error);
        });
        navigate("/app")
        return true; // Signal success
      }
    } catch (err) {
      logger.error("login():signIn() err:", err);
      if (err.name === "UserNotConfirmedException") {
        dispatch({
          type: REGISTER_USER_CONFIRM,
          payload: { username: username },
        });
        navigate("/signup")
        return "CONFIRM"; // Signal that confirmation is needed
      } else {
        dispatch(authError(err));
        return false; // Signal failure
      }
    }
  };
}

// Cognito - currentAuthenticatedUser()
// Cognito - userSession()
// This is a pass-through function to indicate that user has already authenticated
// and has a valid Amplify session.

export function validateUserSession(bypass = true) {
  return function (dispatch) {
    // logger.info("validateUserSession()");

    const checkSession = async () => {
      try {
        const [currentAuthUser, session] = await Promise.all([
          getCurrentUser(),
          fetchAuthSession(),
        ]);

        // logger.info("validateUserSession(): currentAuthenticatedUser() currentAuthUser:", currentAuthUser);
        // logger.info("actions.validateUserSession(): userSession() session:", session);

        // With Amplify v6, if fetchAuthSession() succeeds, the session is valid.
        // It automatically handles token refresh. An error is thrown if it fails.
        dispatch({ type: AUTH_USER, currentAuthUser });

        // const credentials = session.credentials;
        // if (credentials) {
        //   logger.warn("ACCESSKEYID:", credentials.accessKeyId);
        //   logger.warn("SESSIONTOKEN:", credentials.sessionToken);
        //   logger.warn("SECRET ACCESS KEY:", credentials.secretAccessKey);
        // }

        patch(`/user/updatedLastAccessed`, {}).catch(err => {
          logger.error("validateUserSession():updatedLastAccessed err:", err);
        });

      } catch (err) {
        // This is the expected exception when no user is logged in.
        // We can log it for info but avoid treating it as a critical error.
        if (err.name === 'UserUnAuthenticatedException') {
          logger.info("validateUserSession(): No active session found. User is not authenticated.");
        } else {
          // Log other, unexpected errors.
          logger.error("validateUserSession() unexpected error:", err);
        }
        
        Cache.clear();
        dispatch({ type: UNAUTH_USER });
        dispatch({ type: RESET_APP });
      }
    };

    checkSession();
  };
}

// Cognito - completeNewPassword()
export function setNewPassword(cognitoUser, newPassword, navigate) {
  return async function (dispatch) {
    logger.info("setNewPassword(): cognitoUSer, newPassword:", {
      cognitoUser,
      newPassword,
    });

    // completeNewPassword (cognito)
    return confirmSignIn({ user: cognitoUser, challengeResponse: newPassword })
      .then(async (data) => {
        logger.info("setNewPassword():completeNewPassword() data: ", data);

        // inspect response 'data' and check whether
        // 1. MFA confirmation is required, dispatch->AUTH_MFA
        // 2. otherwise, authenticate user, dispatch -> AUTH_USER
        if (data.challengeName === "SMS_MFA") {
          dispatch({ type: AUTH_MFA, payload: data });
        } else {
          // On successful password set, get the current user object to correctly set the auth state.
          const currentAuthUser = await getCurrentUser();
          dispatch({ type: AUTH_USER, currentAuthUser });

          //for first time login new passwords
          patch(`/user/updatedLastAccessed`, {}).catch((err) => {
            logger.error("setNewPassword():updatedLastAccessed err:", err);
            });
          navigate("/app")
          return true; // Signal success
        }
      })
      .catch((err) => {
        logger.error("setNewPassword():completeNewPassword() err:", err);
        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
        return false; // Signal failure
      });
  };
}

// Cognito - signOut()
export function logout(navigate, message) {
  return async function (dispatch) {
    logger.info("logout()");
    try {
      console.log("Amplify config inside logout:", Amplify.getConfig());
      await signOut();
      logger.info("logout():signOut() success");

      dispatch({ type: UNAUTH_USER });
      dispatch({ type: RESET_APP });

      let db = new dexie("Jobs");
      await db.delete();
      Cache.clear();
      if(navigate){
        navigate("/")
      }
      if (message === "inactivity") {
        window.location.assign("/login?message=inactivity");
      } else {
        window.location.assign("/"); // Use assign to ensure a full page refresh and state clearing.
      }
    } catch (err) {
      logger.error("logout():signOut() err:", err);
      dispatch(authError(err));
    }
  };
}

// Cognito - confirmSignIn()
export function confirmLogin({ code }) {
  return async function (dispatch, getState) {
    const { auth: { currentAuthUser: cognitoUser } } = getState();
    logger.info("confirmLogin(): code:", {
      code,
    });

    // confirmSignIn (cognito)
    return confirmSignIn({ challengeResponse: code, user: cognitoUser })
      .then(async (data) => {
        logger.info("confirmLogin():confirmSignIn() data: ", data);

        const currentAuthUser = await getCurrentUser();
        dispatch({ type: AUTH_USER, currentAuthUser });

        return true; // Signal success
      })
      .catch((err) => {
        logger.error("confirmLogin():confirmSignIn() err:", err);
        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
        return false; // Signal failure
      });
  };
}

// Cognito - signUp()
export function register({ username, password, email, phone, given_name, family_name, captcha }) {
  return async function (dispatch) {
    logger.info("register(): username, email, phone: ", {
      username,
      email,
      phone,
    });

    try {
      const origin = await Cache.getItem("origin") || {};
      const {
        organisation = "skillzminer",
        contract = "sm",
        provider = "skillzminer",
      } = origin;

      const { nextStep } = await signUp({
        username,
        password,
        options: {
          userAttributes: {
            email: email,
            phone_number: phone,
            given_name,
            family_name,
            name: `${given_name} ${family_name}`,
            "custom:organisation": organisation,
            "custom:contract": contract,
            "custom:origin": provider,
          },
          clientMetadata: {
            'captcha': captcha
          }
        }
      });

      logger.info("register(): signUp() success:", nextStep);
      if (nextStep.signUpStep === 'CONFIRM_SIGN_UP') {
        dispatch({ type: REGISTER_USER_CONFIRM, payload: { username } });
        return "CONFIRM";
      } else if (nextStep.signUpStep === 'DONE') {
        dispatch({ type: REGISTER_USER });
        return "SUCCESS";
      }

    } catch (err) {
      logger.error("register():signUp() err:", err);
      // The error handling logic remains the same.
      dispatch(authError(err));
      return "ERROR";
    }
  };
}

// Cognito - confirmSignUp()
export function confirmRegistration({ username, code }, navigate) {
  return async function (dispatch) {
    logger.info("confirmRegistration(): cognitoUSer, code:", {
      username,
      code,
    });

    // confirmSignUp (cognito)
    confirmSignUp({ username, confirmationCode: code })
      .then((data) => {
        logger.info("confirmRegistration():confirmSignUp() data: ", data);

        // A successful registration response doesnt contain idToken.
        // So we must redirect to login screen

        // dispatch REGISTER_USER_CONFIRM
        //dispatch({ type: REGISTER_USER_CONFIRM });
        dispatch({ type: AUTH_CONFIRM_SUCCESS });
        navigate("/login");
        return true; // Signal success
      })
      .catch((err) => {
        logger.error("confirmRegistration():confirmSignUp() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        //dispatch(authError(err));
        dispatch({
          type: REGISTER_USER_ERROR,
          payload: err.message,
          username,
        });
        return false; // Signal failure
      });
  };
}

// Cognito - resendSignUp()
export function resendConfirmationCode(username) {
  return function (dispatch) {
    logger.info("resendConfirmationCode(): username: ", username);

    // resendSignUp (cognito)
    resendSignUpCode({ username })
      .then((data) => {
        logger.info("resendConfirmationCode():resendSignUp() data:", data);

        dispatch({ type: CODE_RESEND_SUCCESS });
      })
      .catch((err) => {
        logger.error(
          "resendConfirmationCode():forgotPasswordSubmit() err:",
          err
        );

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
      });
  };
}

// Cognito - forgotPassword()
export function forgotPassword({ username }) {
  return async function (dispatch) {
    logger.info("forgotPassword(): username: ", { username });

    // forgotPassword (cognito)
    return resetPassword({ username })
      .then((data) => {
        logger.info("forgotPassword():forgotPassword() data:", data);

        dispatch({ type: FORGOT_PASSWORD });
        return true; // Signal success
      })
      .catch((err) => {
        logger.error("forgotPassword():forgotPassword() err:", err);

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
        return false; // Signal failure
      });
  };
}

// Cognito - forgotPasswordSubmit()
export function confirmForgotPassword(
  { username, code, newPassword },
  navigate
) {
  return async function (dispatch) {
    logger.info("confirmForgotPassword(): username, code, newPassword: ", {
      username,
      code,
      newPassword,
    });

    // forgotPasswordSubmit (cognito)
    return confirmResetPassword({ username, confirmationCode: code, newPassword })
      .then((data) => {
        logger.info(
          "confirmForgotPassword():forgotPasswordSubmit() data:",
          data
        );

        // TODO - User password changed successfully, do we need to login again?
        dispatch({ type: FORGOT_PASSWORD_CONFIRM });
        navigate('/login');
        return true; // Signal success
      })
      .catch((err) => {
        logger.error(
          "confirmForgotPassword():forgotPasswordSubmit() err:",
          err
        );

        // error -- invoke authError which dispatches AUTH_ERROR
        dispatch(authError(err));
        return false; // Signal failure
      });
  };
}

export function unsubscribe(email) { //uses new unauthed proxy
  return async function (dispatch) {
    logger.info("unsubscribe(): email: ", { email });
    await post(`/unauth/unsubscribe`, { email: email });
  };
}

export function subscribe(email) {
  return async function (dispatch) {
    logger.info("subscribe(): email: ", { email });
    await post(`/user/subscribe`, { email: email });
  };
}


export function autoApply(userID, sort) {
  return async function (dispatch) {
    const { jobitem } = await post(`/unauth/jobitems/get`, { userID: userID, sort: sort });


    if (jobitem){
      let { jobTitle, occupation, qual_name, rangeKey, vacancy_flag, job_url, jobId, searchID, project_flag} = jobitem;
      let isQualification = false

      if (qual_name) {
        isQualification = true;
        jobTitle = qual_name;
        rangeKey = jobitem.qual_awarding_org + "#" + occupation + "#" + jobitem.jobId;
      }
      if(project_flag){
        rangeKey = searchID + "#" +jobId
      }
      let body = {
        rangeKey: rangeKey,
        jobitem: jobitem,
        isQualification,
        userID: userID,
        doVacancyApply: vacancy_flag ? true : false,
        jobId: jobId
      }
      await post(`/unauth/jobitems/applied`, body);

      if (job_url && job_url !== "https://dev-pwa.skillzminer.io/login"){
        return job_url.replace(/^(?!https?:\/\/)/, "https://");
      } 
    }
  }
}

export function updateJobTracking(userID, sort, updateAction) {  //uses new unauthed proxy
  return async function (dispatch) {
    const { jobitem, jobitemType } = await post(`/unauth/jobitems/get`, { userID: userID, sort: sort });
    
    if (jobitem){
      let { jobTitle, occupation, qual_name, rangeKey } = jobitem;
      let isQualification = false

      if (qual_name) {
        isQualification = true;
        jobTitle = qual_name;
        rangeKey = jobitem.qual_awarding_org + "#" + occupation + "#" + jobitem.jobId;
      }

      let body = {
        rangeKey: rangeKey,
        jobitem: jobitem,
        isQualification,
        verified: true,
        recreate:false,
        userID: userID
      }

      switch(updateAction) {
        case "didntapply":
          if (jobitemType === "applied"){
            await del(`/unauth/jobitems/applied`, body);
          }
          else{
            await del(`/unauth/jobitems/interviewed`, body);
          }
          break;
        case "haventappliedyet":
          body.verified = false
          body.recreate = true
          body.applicationDelay = true
          await post(`/unauth/jobitems/applied`, body);
          // need to make a new item for this
          break;
        case "applied":
          //need to add verified thing,  also check if already existing 
          await post(`/unauth/jobitems/applied`, body);
          break;
        case "interviewed":
          await post(`/unauth/jobitems/interviewed`, body);
          break;
        case "started":
          await post(`/unauth/jobitems/started`, body);
          break;
        case "wasntsuccessful":
          await post(`/unauth/jobitems/unsuccessful`, body);
          break;
        default:
          break;
      }
   }
  };
}